/**
 * Bank confirmation letter — SightHost.
 *
 * A formal, single-page "Confirmation of Banking Details" on letterhead, for a
 * sales agent to hand a prospect who asks "how do I know this account is really
 * yours?". That question is not paranoia: invoice-redirection fraud in South
 * Africa works precisely by intercepting a supplier's details and substituting
 * a mule account, and a WhatsApp message with an account number in it is the
 * easiest thing in the world to forge.
 *
 * Three deliberate properties follow from that, and none of them are decoration:
 *
 *   1. **It states that these are the ONLY details, and that they never change.**
 *      A customer who has read that sentence has a reason to phone when a
 *      second set arrives.
 *   2. **It gives a verification route that is not the letter itself** — the
 *      published page and the switchboard number. A fraud warning that says
 *      "check with us" and then supplies only the contact details on the forged
 *      document is theatre.
 *   3. **It names the issuing agent.** An unsigned letter from a company the
 *      customer has never dealt with carries no more weight than the WhatsApp
 *      message it was meant to replace.
 *
 * The letter carries NO amount and NO invoice reference — it is a standing
 * confirmation of identity, not a request for payment. Keeping those apart is
 * what lets an agent send it early in a conversation without it reading as a
 * demand.
 *
 * Rendered client-side by the sales portal (see BankConfirmationLetterCard).
 */

import * as React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { documentStyles, colors, formatPDFDate } from './styles';

export interface BankLetterCompany {
  companyName: string;
  legalName?: string;
  registrationNumber?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
}

export interface BankLetterBank {
  name: string;
  accountName: string;
  accountNumber: string;
  branchCode: string;
  accountType?: string;
  swiftCode?: string;
}

export interface BankLetterIssuer {
  /** Agent / staff member's full name. Falls back to the company when absent. */
  name?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface BankConfirmationLetterProps {
  company: BankLetterCompany;
  bank: BankLetterBank;
  issuer?: BankLetterIssuer;
  /** Optional named addressee. Falls back to "To whom it may concern". */
  addressee?: string | null;
  /** ISO date; defaults to today at render time. */
  issuedAt?: string;
  /** Public page a recipient can check the details against, independently. */
  verifyUrl?: string;
}

const s = StyleSheet.create({
  meta: {
    fontSize: 9,
    color: colors.secondary,
    textAlign: 'right',
    lineHeight: 1.5,
  },
  date: {
    fontSize: 9,
    color: colors.secondary,
    marginTop: 18,
    marginBottom: 14,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    letterSpacing: 0.6,
    marginBottom: 4,
  },
  rule: {
    borderBottomWidth: 2,
    borderBottomColor: colors.accent,
    marginBottom: 16,
    width: 150,
  },
  addressee: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginBottom: 12,
  },
  body: {
    fontSize: 9.5,
    color: '#1f2937',
    lineHeight: 1.6,
    marginBottom: 11,
  },
  bankBox: {
    borderWidth: 1,
    borderColor: colors.border,
    borderLeftWidth: 3,
    borderLeftColor: colors.accent,
    backgroundColor: colors.accentLight,
    padding: 14,
    marginTop: 4,
    marginBottom: 16,
  },
  bankRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  bankLabel: {
    width: 120,
    fontSize: 9,
    color: colors.secondary,
  },
  bankValue: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
  },
  warnBox: {
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.headerBg,
    padding: 12,
    marginBottom: 18,
  },
  warnTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginBottom: 4,
  },
  warnBody: {
    fontSize: 8.5,
    color: '#1f2937',
    lineHeight: 1.55,
  },
  signBlock: {
    marginTop: 8,
  },
  signName: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginTop: 16,
  },
  signMeta: {
    fontSize: 9,
    color: colors.secondary,
    marginTop: 1.5,
  },
});

export function BankConfirmationLetter({
  company,
  bank,
  issuer,
  addressee,
  issuedAt,
  verifyUrl,
}: BankConfirmationLetterProps) {
  // Rendered fresh on each download, so "today" is the date the agent sent it.
  const issued = issuedAt ?? new Date().toISOString();
  const legal = company.legalName ?? company.companyName;

  return (
    <Document
      title={`Confirmation of Banking Details — ${legal}`}
      author={legal}
      subject="Confirmation of Banking Details"
    >
      <Page size="A4" style={documentStyles.page}>
        {/* Letterhead */}
        <View style={documentStyles.header}>
          <View>
            {company.logoUrl ? (
              <Image src={company.logoUrl} style={documentStyles.logo} />
            ) : (
              <Text style={documentStyles.companyName}>{company.companyName}</Text>
            )}
          </View>
          <View>
            <Text style={s.meta}>{legal}</Text>
            {company.registrationNumber ? (
              <Text style={s.meta}>Reg. No. {company.registrationNumber}</Text>
            ) : null}
            {company.address ? <Text style={s.meta}>{company.address}</Text> : null}
            {company.phone ? <Text style={s.meta}>{company.phone}</Text> : null}
            {company.email ? <Text style={s.meta}>{company.email}</Text> : null}
            {company.website ? <Text style={s.meta}>{company.website}</Text> : null}
          </View>
        </View>

        <Text style={s.date}>{formatPDFDate(issued)}</Text>

        <Text style={s.title}>CONFIRMATION OF BANKING DETAILS</Text>
        <View style={s.rule} />

        <Text style={s.addressee}>
          {addressee ? `Dear ${addressee}` : 'TO WHOM IT MAY CONCERN'}
        </Text>

        <Text style={s.body}>
          We confirm that the bank account set out below is the only account
          operated by {legal} for the receipt of customer payments. All invoices
          and quotations issued by us are payable into this account and no other.
        </Text>

        <View style={s.bankBox}>
          <View style={s.bankRow}>
            <Text style={s.bankLabel}>Bank</Text>
            <Text style={s.bankValue}>{bank.name}</Text>
          </View>
          <View style={s.bankRow}>
            <Text style={s.bankLabel}>Account holder</Text>
            <Text style={s.bankValue}>{bank.accountName}</Text>
          </View>
          <View style={s.bankRow}>
            <Text style={s.bankLabel}>Account number</Text>
            <Text style={s.bankValue}>{bank.accountNumber}</Text>
          </View>
          <View style={s.bankRow}>
            <Text style={s.bankLabel}>Branch code</Text>
            <Text style={s.bankValue}>{bank.branchCode}</Text>
          </View>
          {bank.accountType ? (
            <View style={s.bankRow}>
              <Text style={s.bankLabel}>Account type</Text>
              <Text style={s.bankValue}>{bank.accountType}</Text>
            </View>
          ) : null}
          {bank.swiftCode ? (
            <View style={s.bankRow}>
              <Text style={s.bankLabel}>SWIFT / BIC</Text>
              <Text style={s.bankValue}>{bank.swiftCode}</Text>
            </View>
          ) : null}
        </View>

        <Text style={s.body}>
          Please quote your invoice or quotation number as the payment reference.
          Payments made without a reference can take longer to allocate to your
          account.
        </Text>

        {/* The verification route is deliberately independent of this document:
            a forged copy would carry the forger's contact details, so pointing
            only at "the address above" would be worthless. */}
        <View style={s.warnBox}>
          <Text style={s.warnTitle}>Important — protect yourself against payment fraud</Text>
          <Text style={s.warnBody}>
            These banking details do not change. {legal} will never notify you of
            a change of bank account by email, SMS or WhatsApp. If you receive any
            communication claiming that our banking details have changed, treat it
            as fraudulent and do not make payment.
            {verifyUrl
              ? ` You can verify these details at any time at ${verifyUrl}, or by phoning us on the number on our published website.`
              : ' You can verify these details at any time by phoning us on the number on our published website.'}
            {' '}Always confirm by voice, using a number you have obtained
            independently, before paying a new or amended account.
          </Text>
        </View>

        <Text style={s.body}>Yours faithfully,</Text>

        <View style={s.signBlock}>
          <Text style={s.signName}>{issuer?.name?.trim() || legal}</Text>
          {issuer?.role ? <Text style={s.signMeta}>{issuer.role}</Text> : null}
          <Text style={s.signMeta}>{legal}</Text>
          {issuer?.email ? <Text style={s.signMeta}>{issuer.email}</Text> : null}
          {issuer?.phone ? <Text style={s.signMeta}>{issuer.phone}</Text> : null}
        </View>

        <View style={documentStyles.footer} fixed>
          <Text style={documentStyles.footerText}>
            This letter confirms banking details only. It is not an invoice and
            not a demand for payment.
          </Text>
          <Text style={documentStyles.footerCopyright}>
            {legal}
            {company.registrationNumber ? ` · Reg. No. ${company.registrationNumber}` : ''}
            {' · Issued '}
            {formatPDFDate(issued)}
          </Text>
        </View>
      </Page>
    </Document>
  );
}
